import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

const SLIDES = [
  {
    id: 'employee-performance',
    title: 'Employee Performance Agent',
    agentName: 'Ops Insight Agent',
    userPrompt:
      'Please share this month performance stats for John and Sarah, including quality, completion rate, and punctuality.',
    thinkingSteps: ['Understanding request', 'Reviewing KPI definitions', 'Preparing summary format'],
    toolSteps: [
      'Tool: HR Metrics Lookup',
      'Tool: Attendance Monitor',
      'Tool: Performance Scoring Template',
    ],
    finalResponse: `Here is a performance snapshot for this month:

John
- Output quality: 91/100
- Task completion rate: 94%
- Punctuality: 97%
- Summary: Strong and consistent performer with reliable delivery speed.

Sarah
- Output quality: 95/100
- Task completion rate: 89%
- Punctuality: 93%
- Summary: High quality work with room to improve completion pace on larger tasks.

Recommendation:
Both employees are performing well. John is strongest in consistency and on-time completion, while Sarah leads on quality. A balanced mentoring plan can help Sarah improve completion rate without reducing quality.`,
  },
  {
    id: 'support-response',
    title: 'Customer Support Agent',
    agentName: 'Care Response Agent',
    userPrompt:
      'Write a response to a frustrated customer whose order arrived late and who says they are very disappointed.',
    thinkingSteps: ['Reading customer tone', 'Checking resolution options', 'Drafting empathetic response'],
    toolSteps: ['Tool: Support Policy Guide', 'Tool: Order Resolution Playbook', 'Tool: Tone Assistant'],
    finalResponse: `Hi [Customer Name],

I am really sorry for the delay and the frustration this caused. You trusted us to deliver on time, and we did not meet that expectation.

I have already reviewed your order and escalated it with our shipping team. To make this right, we can offer:
- A priority follow-up on your shipment status
- A refund of the delivery fee
- A 15% discount code for your next order

If you prefer a different resolution, reply here and I will personally handle it right away.

Thank you for your patience, and again, we are sorry for the inconvenience.

Best regards,
[Support Team]`,
  },
  {
    id: 'client-email',
    title: 'Client Update Email Agent',
    agentName: 'Client Communications Agent',
    userPrompt:
      'Draft a professional client update email about project progress, current status, and next milestones.',
    thinkingSteps: ['Collecting project highlights', 'Structuring email sections', 'Finalizing clear next steps'],
    toolSteps: ['Tool: Project Tracker', 'Tool: Milestone Planner', 'Tool: Email Template Builder'],
    finalResponse: `Subject: Project Update - Progress and Upcoming Milestones

Hi [Client Name],

I hope you are doing well. I wanted to share a quick update on the project:

Current Progress
- Core feature implementation is complete.
- Internal QA for the latest build has passed.
- Performance optimization has started for the dashboard module.

Current Status
- The project remains on schedule.
- No critical blockers at this time.
- Two minor UI refinements are in progress and will be included in the next release candidate.

Upcoming Milestones
- Staging deployment: [Date]
- Client review session: [Date]
- Final revision window: [Date]

Please let me know if you would like us to prioritize any specific items before the review session.

Best regards,
[Your Name]
[Role / Team]`,
  },
  {
    id: 'weather',
    title: 'Weather Agent',
    agentName: 'Weather Snapshot Agent',
    userPrompt: 'What is the weather today? Give me a quick summary I can use before heading out.',
    thinkingSteps: ['Reading weather request', 'Checking key conditions', 'Preparing concise forecast summary'],
    toolSteps: ['Tool: Local Forecast Feed', 'Tool: Wind and Humidity Panel', 'Tool: Daily Advisory Formatter'],
    finalResponse: `Today weather summary :

- Condition: Partly cloudy
- Temperature: 28 C
- Feels like: 31 C
- Humidity: 64%
- Wind: 14 km/h from the southwest
- Rain chance: 20%

Recommendation:
It should be warm with light wind. A light layer and water bottle are a good idea if you will be outside for a long period.`,
  },
]

const WAIT = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const AIAgentDemoSection = () => {
  const [activeSlide, setActiveSlide] = useState(0)
  const [showUserPrompt, setShowUserPrompt] = useState(false)
  const [showThinking, setShowThinking] = useState(false)
  const [visibleThinkingCount, setVisibleThinkingCount] = useState(0)
  const [visibleToolCount, setVisibleToolCount] = useState(0)
  const [typedResponse, setTypedResponse] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [activeToolIndex, setActiveToolIndex] = useState(-1)
  const [completed, setCompleted] = useState(false)

  const currentSlide = useMemo(() => SLIDES[activeSlide], [activeSlide])

  useEffect(() => {
    let cancelled = false

    const runPreview = async () => {
      setShowUserPrompt(false)
      setShowThinking(false)
      setVisibleThinkingCount(0)
      setVisibleToolCount(0)
      setTypedResponse('')
      setIsTyping(false)
      setActiveToolIndex(-1)
      setCompleted(false)

      await WAIT(260)
      if (cancelled) return
      setShowUserPrompt(true)

      await WAIT(360)
      if (cancelled) return
      setShowThinking(true)

      for (let i = 0; i < currentSlide.thinkingSteps.length; i += 1) {
        if (cancelled) return
        setVisibleThinkingCount(i + 1)
        await WAIT(380)
      }

      for (let i = 0; i < currentSlide.toolSteps.length; i += 1) {
        if (cancelled) return
        setVisibleToolCount(i + 1)
        setActiveToolIndex(i)
        await WAIT(650)
      }

      if (cancelled) return
      setIsTyping(true)
      setActiveToolIndex(-1)

      for (let i = 1; i <= currentSlide.finalResponse.length; i += 1) {
        if (cancelled) return
        setTypedResponse(currentSlide.finalResponse.slice(0, i))
        await WAIT(8)
      }

      if (cancelled) return
      setIsTyping(false)
      setCompleted(true)
    }

    runPreview()

    return () => {
      cancelled = true
    }
  }, [currentSlide])

  const showPrevious = () => {
    setActiveSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length)
  }

  const showNext = () => {
    setActiveSlide((prev) => (prev + 1) % SLIDES.length)
  }

  return (
    <section className="relative overflow-hidden py-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(249,193,37,0.1),transparent_36%),radial-gradient(circle_at_82%_28%,rgba(249,193,37,0.04),transparent_40%)]" />

      <div className="container-main relative z-10">
        <div className="mx-auto max-w-4xl text-center">
          <span className="section-kicker">Preview Demo</span>
          <h1 className="mt-4 text-4xl font-bold text-white md:text-5xl">4 AI Agent Preview Flows</h1>
          <p className="mt-3 text-lg text-[#b8b8b8]">
            A video-style preview of four chat agents.
          </p>
          <p className="mt-3 text-sm text-[#939393]">
            The preview auto-plays each scene. Use arrows to move between sections.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-white/10 bg-[#101010] p-4 shadow-[0_20px_45px_rgba(0,0,0,0.35)] md:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9f9f9f]">{currentSlide.agentName}</p>
              <h2 className="mt-1 text-2xl font-semibold text-white">{currentSlide.title}</h2>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={showPrevious}
                className="h-10 w-10 rounded-full border border-white/20 bg-white/5 text-lg font-semibold text-white hover:border-[rgb(249,193,37)]/50 hover:text-[rgb(249,193,37)]"
                aria-label="Previous demo section"
              >
                {'<'}
              </button>
              <span className="min-w-[62px] text-center text-sm font-semibold text-[#d7d7d7]">
                {activeSlide + 1} / {SLIDES.length}
              </span>
              <button
                type="button"
                onClick={showNext}
                className="h-10 w-10 rounded-full border border-white/20 bg-white/5 text-lg font-semibold text-white hover:border-[rgb(249,193,37)]/50 hover:text-[rgb(249,193,37)]"
                aria-label="Next demo section"
              >
                {'>'}
              </button>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
            <article className="rounded-2xl border border-white/10 bg-[#141414] p-4 md:p-5">
              <h3 className="mb-4 text-lg font-semibold text-white">Conversation Preview</h3>

              <div className="space-y-4">
                {showUserPrompt && (
                  <div className="flex justify-end">
                    <div className="max-w-[95%]">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#9a9a9a]">You</p>
                      <div className="rounded-2xl bg-[rgb(249,193,37)] px-4 py-3 text-sm font-medium leading-relaxed text-[#121212]">
                        {currentSlide.userPrompt}
                      </div>
                    </div>
                  </div>
                )}

                {(typedResponse || isTyping) && (
                  <div className="flex justify-start">
                    <div className="max-w-[95%]">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#9a9a9a]">Agent</p>
                      <div className="rounded-2xl border border-white/10 bg-[#181818] px-4 py-3 text-sm leading-relaxed text-[#e8e8e8]">
                        <p className="whitespace-pre-line">
                          {typedResponse}
                          {isTyping && <span className="ml-1 inline-block animate-pulse text-[rgb(249,193,37)]">|</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </article>

            <article className="rounded-2xl border border-white/10 bg-[#141414] p-4 md:p-5">
              <h3 className="text-lg font-semibold text-white">Agent Activity</h3>
              <p className="mt-1 text-sm text-[#a6a6a6]">Simulated thinking and tool calls</p>

              {!showThinking && (
                <div className="mt-4 rounded-xl border border-white/10 bg-[#161616] p-3">
                  <p className="text-sm text-[#9b9b9b]">Agent is preparing to analyze the request...</p>
                </div>
              )}

              {showThinking && (
                <>
                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#8f8f8f]">Thinking</p>
                    <div className="space-y-2">
                      {currentSlide.thinkingSteps.map((step, index) => {
                        const visible = index < visibleThinkingCount
                        return (
                          <div
                            key={step}
                            className={`rounded-xl border px-3 py-2 text-sm transition-all ${
                              visible
                                ? 'border-[rgb(249,193,37)]/35 bg-[rgb(249,193,37)]/10 text-[rgb(249,193,37)]'
                                : 'border-white/10 bg-[#161616] text-[#8f8f8f]'
                            }`}
                          >
                            {step}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#8f8f8f]">Tools</p>
                    <div className="space-y-2">
                      {currentSlide.toolSteps.map((step, index) => {
                        const visible = index < visibleToolCount
                        const active = activeToolIndex === index

                        return (
                          <div
                            key={step}
                            className={`rounded-xl border px-3 py-2 text-sm transition-all ${
                              active
                                ? 'border-[rgb(249,193,37)]/45 bg-[rgb(249,193,37)]/12 text-[rgb(249,193,37)]'
                                : visible
                                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                                  : 'border-white/10 bg-[#161616] text-[#8f8f8f]'
                            }`}
                          >
                            {step}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}

              {completed && (
                <div className="mt-4 rounded-xl border border-white/10 bg-[#161616] p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8f8f8f]">Status</p>
                  <p className="mt-1 text-sm font-semibold text-emerald-300">Preview complete for this section</p>
                </div>
              )}
            </article>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-5xl rounded-2xl border border-white/10 bg-[#101010] p-4 md:p-6">
          <h3 className="text-xl font-semibold text-white">How Agents Work (Simple View)</h3>
          <p className="mt-2 text-sm text-[#a6a6a6]">
            Agents follow a basic loop: understand request, use tools, then return a clear result.
          </p>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-[#141414] p-4">
              <p className="mb-3 text-sm font-semibold text-white">Diagram 1: Single Agent Flow</p>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">User Request</span>
                <span className="text-[#f9c125]">{'->'}</span>
                <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Agent Thinks</span>
                <span className="text-[#f9c125]">{'->'}</span>
                <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Uses Tools</span>
                <span className="text-[#f9c125]">{'->'}</span>
                <span className="rounded-lg border border-[rgb(249,193,37)]/40 bg-[rgb(249,193,37)]/12 px-3 py-2 text-[rgb(249,193,37)]">
                  Final Reply
                </span>
              </div>
              <p className="mt-3 text-sm text-[#a6a6a6]">
                The agent first understands the user goal, then reasons through the task, checks the right tools,
                and finally returns a clear answer in a usable format.
              </p>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#141414] p-4">
              <p className="mb-3 text-sm font-semibold text-white">Diagram 2: Multi-Agent Handoff</p>
              <div className="space-y-2 text-xs font-semibold">
                <div className="flex items-center gap-2">
                  <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Coordinator Agent</span>
                  <span className="text-[#f9c125]">{'->'}</span>
                  <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Support Agent</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Coordinator Agent</span>
                  <span className="text-[#f9c125]">{'->'}</span>
                  <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Email Agent</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-lg border border-white/15 bg-[#161616] px-3 py-2 text-[#d7d7d7]">Coordinator Agent</span>
                  <span className="text-[#f9c125]">{'->'}</span>
                  <span className="rounded-lg border border-[rgb(249,193,37)]/40 bg-[rgb(249,193,37)]/12 px-3 py-2 text-[rgb(249,193,37)]">
                    Combined Response
                  </span>
                </div>
              </div>
              <p className="mt-3 text-sm text-[#a6a6a6]">
                A coordinator agent breaks a request into parts, hands each part to specialized agents, then combines
                their outputs into one final response for the user.
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-5xl rounded-2xl border border-[rgb(249,193,37)]/30 bg-[linear-gradient(145deg,rgba(249,193,37,0.11),rgba(17,21,31,0.95))] p-5 md:p-7">
          <h3 className="text-2xl font-semibold text-white">Get Started With Us</h3>
          <p className="mt-2 max-w-3xl text-sm text-[#e6ddc2]">
            We help teams plan, build, and launch practical AI agents for operations, support, sales, and client
            communication. From idea to working flow, we keep everything focused on real business outcomes.
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/15 bg-[#161616] p-3">
              <p className="text-sm font-semibold text-white">1. Discovery</p>
              <p className="mt-1 text-xs text-[#bcbcbc]">We map your workflow and identify high-impact automation points.</p>
            </div>
            <div className="rounded-xl border border-white/15 bg-[#161616] p-3">
              <p className="text-sm font-semibold text-white">2. Build</p>
              <p className="mt-1 text-xs text-[#bcbcbc]">We create agent logic, prompts, tool usage, and response structure.</p>
            </div>
            <div className="rounded-xl border border-white/15 bg-[#161616] p-3">
              <p className="text-sm font-semibold text-white">3. Launch</p>
              <p className="mt-1 text-xs text-[#bcbcbc]">We test, refine, and deploy your agent experience with your team.</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">
              Book a Free Call
            </Link>
            <Link to="/services/ai-chatbots-agents" className="btn-outline">
              Explore Agent Services
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AIAgentDemoSection
